<template>
  <div class="modal fade" id="paymentMethodsModal" tabindex="-1" aria-labelledby="paymentMethodsModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-md">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="paymentMethodsModalLabel">{{ $t('manage_payment_methods') }}</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <table-loading-bar v-if="loading" class="text-center" />
          <div v-else>
            <!-- Add New Payment Method Form -->
            <div class="mb-4">
              <div class="d-flex align-items-center gap-2 mb-3">
                <input 
                  type="text" 
                  class="form-control" 
                  v-model="newPaymentMethod.name" 
                  :placeholder="$t('enter_payment_method_name')"
                  @keyup.enter="addPaymentMethod">
                <button 
                  class="btn btn-success" 
                  @click="addPaymentMethod" 
                  :disabled="!newPaymentMethod.name">
                  <AppIcon name="bi-plus-lg" class="bi bi-plus-lg me-1" />
                </button>
              </div>
              
              <!-- Suggestions -->
              <div class="d-flex flex-wrap gap-2">
                <small class="text-muted me-2">{{ $t('suggestions') }}: </small>
                <span 
                  v-for="suggestion in suggestions" 
                  :key="suggestion" 
                  class="badge bg-light text-dark cursor-pointer border" 
                  @click="useSuggestion(suggestion)"
                  style="cursor: pointer; font-size: 11px;">
                  {{ suggestion }}
                </span>
              </div>
            </div>

            <!-- Payment Methods List -->
            <div class="table-responsive">
              <table class="table table-hover">
                <thead>
                  <tr>
                    <th style="width: 50px;" class="text-center">{{ $t('default') }}</th>
                    <th>{{ $t('name') }}</th>
                    <th>{{ $t('status') }}</th>
                    <th class="text-end">{{ $t('actions') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="method in paymentMethods" :key="method.id">
                    <td class="text-center">
                      <div class="form-check d-flex justify-content-center">
                        <input 
                          class="form-check-input" 
                          type="radio" 
                          name="defaultPaymentMethod" 
                          :checked="method.is_default" 
                          @change="setDefault(method)"
                          style="cursor: pointer;">
                      </div>
                    </td>
                    <td>
                        <input v-if="editingId === method.id" type="text" class="form-control form-control-sm" v-model="method.name">
                        <span v-else>{{ method.name }}</span>
                    </td>
                    <td>
                      <div class="form-check form-switch">
                        <input class="form-check-input" type="checkbox" :checked="method.is_active" @change="toggleStatus(method)" style="cursor: pointer;">
                      </div>
                    </td>
                    <td class="text-end">
                        <div v-if="editingId === method.id" class="d-flex justify-content-end gap-1 ">
                            <button class="btn btn-xs btn-success" style="padding:0.3rem !important" @click="saveEdit(method)">
                                <AppIcon name="bi-check" class="bi bi-check" />
                            </button>
                            <button class="btn btn-xs btn-secondary" style="padding:0.3rem !important" @click="cancelEdit">
                                <AppIcon name="bi-x" class="bi bi-x" />
                            </button>
                        </div>
                        <div v-else class="d-flex justify-content-end gap-1">
                            <button class="btn btn-sm btn-outline-primary" style="padding:0.2rem 0.3rem !important" @click="startEdit(method)">
                                <AppIcon name="bi-pencil" class="bi bi-pencil" />
                            </button>
                            <button class="btn btn-sm btn-outline-danger" style="padding:0.2rem 0.3rem !important" @click="deletePaymentMethod(method)">
                                <AppIcon name="bi-trash" class="bi bi-trash" />
                            </button>
                        </div>
                    </td>
                  </tr>
                  <tr v-if="paymentMethods.length === 0">
                    <td colspan="4" class="text-center py-4 text-muted">
                      {{ $t('no_payment_methods_found') }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">{{ $t('close') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import helper from '@/helper'

export default {
  name: 'PaymentMethodsModal',
  data() {
    return {
      loading: false,
      paymentMethods: [],
      newPaymentMethod: {
        name: '',
        is_active: true
      },
      suggestions: ['Cash', 'Cheque', 'Bank Transfer', 'Credit Card', 'Debit Card', 'Mobile Money', 'PayPal', ' Lemon Squeezy'],
      editingId: null,
      originalMethod: null
    }
  },
  methods: {
    async fetchPaymentMethods() {
      this.loading = true
      try {
        const response = await helper.customGet(`${process.env.VUE_APP_API}/payment-methods?per_page=100`)
        this.paymentMethods = response.data.data

        // Check if there is a default payment method
        const hasDefault = this.paymentMethods.some(m => m.is_default)
        
        if (!hasDefault && this.paymentMethods.length > 0) {
            // Try to find 'cash'
            const cashMethod = this.paymentMethods.find(m => m.name.toLowerCase() === 'cash')
            
            if (cashMethod) {
                await helper.customPatch(`${process.env.VUE_APP_API}/payment-methods/${cashMethod.id}`, {
                    is_default: true
                }, { showToast: false })
                
                // Update local state
                cashMethod.is_default = true
            }
        }
      } catch (error) {
        alert(error)
      } finally {
        this.loading = false
      }
    },
    async addPaymentMethod() {
      if (!this.newPaymentMethod.name) return

      try {
        await helper.customPost(`${process.env.VUE_APP_API}/payment-methods`, this.newPaymentMethod, { showToast: true })
        this.newPaymentMethod = {
          name: '',
          is_active: true
        }
        await this.fetchPaymentMethods()
      } catch (error) {
        alert(error)
      }
    },
    async deletePaymentMethod(method) {
      if (!confirm(this.$t('are_you_sure'))) return

      try {
        await helper.customDelete(`${process.env.VUE_APP_API}/payment-methods/${method.id}`, { showToast: true })
        this.paymentMethods = helper.withoutDeletedRecords(this.paymentMethods, method.id)
      } catch (error) {
        alert(error)
      }
    },
    async toggleStatus(method) {
      try {
        await helper.customPatch(`${process.env.VUE_APP_API}/payment-methods/${method.id}`, {
          is_active: !method.is_active
        }, { showToast: true })
        await this.fetchPaymentMethods()
      } catch (error) {
        alert(error)
      }
    },
    async setDefault(method) {
      try {
        await helper.customPatch(`${process.env.VUE_APP_API}/payment-methods/${method.id}`, {
          is_default: true
        }, { showToast: true })
        await this.fetchPaymentMethods()
      } catch (error) {
        alert(error)
      }
    },
    useSuggestion(suggestion) {
      this.newPaymentMethod.name = suggestion
    },
    startEdit(method) {
        this.editingId = method.id
        this.originalMethod = JSON.parse(JSON.stringify(method))
    },
    cancelEdit() {
        const index = this.paymentMethods.findIndex(m => m.id === this.editingId)
        if (index !== -1) {
            this.$set(this.paymentMethods, index, this.originalMethod)
        }
        this.editingId = null
        this.originalMethod = null
    },
    async saveEdit(method) {
        try {
            await helper.customPatch(`${process.env.VUE_APP_API}/payment-methods/${method.id}`, {
                name: method.name
            }, { showToast: true })
            this.editingId = null
            this.originalMethod = null
            await this.fetchPaymentMethods()
        } catch (error) {
            alert(error)
        }
    },
    show() {
        const modal = new window.bootstrap.Modal(document.getElementById('paymentMethodsModal'))
        modal.show()
        this.fetchPaymentMethods()
    }
  },
  mounted() {
    // Event listener for modal open if needed, but show() method handles it
  }
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
